import { FolderNodePublishBar } from 'components/Folder/FolderNodePublishBar';
import React, { FC } from 'react';
import { AppFileTypes, mimeTypeToAppType } from 'utils/file/MIMETypeUtils';
import { graphql, useFragment } from 'utils/graphClient';
import { PDFTronFileRenderer } from './PDFTronFileRenderer';
import { PrepareSignatureFileRenderer } from './PrepareSignatureFileRenderer';
import { UnsupportedFileRenderer } from './UnsupportedFileRenderer';
import { VideoFileRenderer } from './VideoFileRenderer';
import {
  FileRenderer_node$data,
  FileRenderer_node$key,
} from './__generated__/FileRenderer_node.graphql';
import { REQUEST_SIGNATURE_PARAM } from 'utils/routes';
import { useSearchParams } from 'react-router-dom';
import { SignatureStatusFileRenderer } from './SignatureStatusFileRenderer';
import { useFeatureFlag } from 'utils/sessionProvider';

export const fileNodeFragment = graphql`
  fragment FileRenderer_node on Node {
    ...FolderNodePublishBar_folderNode
    ...ItemOpenedTracker_item
    ...useNodeSecurity_node
    ...DownloadButton_node
    ...SignatureStatusFileRenderer_node
    __typename
    id
    ... on AnnouncementAttachment {
      id
      content {
        __typename
        ... on File {
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
    }
    ... on MemberDetailsAttachment {
      content {
        __typename
        ... on File {
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
    }
    ... on FolderItem {
      id
      name
      canRename {
        value
      }
      content {
        __typename
        ... on File {
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
      canCreateSignatureEnvelope {
        value
      }
    }
    ... on VaultItem {
      id
      content {
        __typename
        ... on File {
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
      canCreateSignatureEnvelope {
        value
      }
    }
    ... on SignableInterface {
      signatureEnvelope {
        id
        status
        signatures {
          nodes {
            id
            member {
              id
            }
            status
          }
        }
        canSign {
          value
        }
        currentFile {
          __typename
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
    }
    ... on VaultDraftItem {
      id
      draftContent_FileRenderer: content {
        __typename
        ... on File {
          id
          name
          fileName
          fileNameWithExtension: fileName(ensureExtension: true)
          mimeType
          url
          size
        }
      }
    }
  }
`;

interface FileRendererMCProps {
  node: FileRenderer_node$key;
}

export const FileRenderer: FC<FileRendererMCProps> = ({ node: nodeRef }) => {
  const node = useFragment(fileNodeFragment, nodeRef);

  if (!node.id) {
    throw new Error('Unexpected content');
  }

  return (
    <>
      <FolderNodePublishBar folderNode={node} />
      <FileRendererDetail folderNode={node} />
    </>
  );
};

export const contentFromNode = (node: FileRenderer_node$data) => {
  let nodeContent;

  switch (node.__typename) {
    case 'VaultItem':
    case 'FolderItem':
      nodeContent =
        node?.signatureEnvelope && node.signatureEnvelope.status !== 'CANCELLED'
          ? node.signatureEnvelope.currentFile
          : node.content;
      break;
    default:
      nodeContent = node.content || node.draftContent_FileRenderer;
  }

  if (nodeContent?.__typename === 'File') {
    return nodeContent;
  }
  return {
    __typename: '%unknown%',
    fileName: '',
    fileNameWithExtension: '',
    id: null,
    mimeType: '',
    name: '',
    size: 0,
    url: null,
  };
};

interface FileRendererFileRendererDetailProps {
  folderNode: FileRenderer_node$data;
}

const FileRendererDetail = ({
  folderNode: node,
}: FileRendererFileRendererDetailProps) => {
  const { id, mimeType } = contentFromNode(node);

  switch (getFileRenderType(mimeType)) {
    case FileRenderType.PDF:
      return <PDFTronFileRendererWithSigningLogic key={id} node={node} />;
    case FileRenderType.DOCUMENT:
      return <PDFTronFileRenderer key={id} node={node} />;
    case FileRenderType.Video:
      return <VideoFileRenderer key={id} node={node} />;
    default:
      return <UnsupportedFileRenderer key={id} node={node} />;
  }
};

const PDFTronFileRendererWithSigningLogic = ({
  node,
}: {
  node: FileRendererFileRendererDetailProps['folderNode'];
}) => {
  const [params] = useSearchParams();

  const isSignaturesEnabled = useFeatureFlag('FEATURE_SIGNATURES');
  if (!isSignaturesEnabled) {
    return <PDFTronFileRenderer node={node} />;
  }

  if (
    node.signatureEnvelope &&
    node.signatureEnvelope.status !== 'CANCELLED' &&
    node.signatureEnvelope.status !== 'COMPLETED'
  ) {
    return <SignatureStatusFileRenderer node={node} />;
  }

  const signatureRequestInterface = !!params.get(REQUEST_SIGNATURE_PARAM);
  if (signatureRequestInterface) {
    return <PrepareSignatureFileRenderer nodeId={node.id} />;
  }

  return <PDFTronFileRenderer node={node} />;
};

export enum FileRenderType {
  PDF,
  DOCUMENT,
  Video,
  Unsupported,
}

export function getFileRenderType(
  mimeType: string | undefined,
): FileRenderType {
  switch (mimeTypeToAppType(mimeType || undefined)) {
    case AppFileTypes.PDF_DOCUMENT:
      return FileRenderType.PDF;
    case AppFileTypes.IMAGE:
    case AppFileTypes.WORD_DOCUMENT:
    case AppFileTypes.EXCEL_DOCUMENT:
    case AppFileTypes.POWERPOINT_DOCUMENT:
      return FileRenderType.DOCUMENT;
    case AppFileTypes.VIDEO:
      return FileRenderType.Video;
    default:
      return FileRenderType.Unsupported;
  }
}
